import {
  FileExcelOutlined,
  FileImageOutlined,
  FileOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  FileWordOutlined,
  FileZipOutlined,
  FolderFilled,
  FolderOutlined,
  RocketOutlined,
} from '@ant-design/icons';
import React from 'react';

/**
 * Get file icon based on type and MIME type
 */
export const getFileIcon = (type, mimeType, filled = false) => {
  if (type === 'folder') {
    const Icon = filled ? FolderFilled : FolderOutlined;
    return <Icon style={{ color: '#faad14', fontSize: 18 }} />;
  }

  // Handle .ccapp files - distinctive purple rocket icon
  if (mimeType === 'application/x-ccapp+json') {
    return <RocketOutlined style={{ color: '#722ed1', fontSize: 18 }} />;
  }

  if (mimeType?.startsWith('image/')) {
    return <FileImageOutlined style={{ color: '#52c41a', fontSize: 18 }} />;
  }
  if (mimeType === 'application/pdf') {
    return <FilePdfOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />;
  }
  if (mimeType?.includes('word') || mimeType?.includes('document')) {
    return <FileWordOutlined style={{ color: '#2f54eb', fontSize: 18 }} />;
  }
  if (mimeType?.includes('excel') || mimeType?.includes('spreadsheet')) {
    return <FileExcelOutlined style={{ color: '#52c41a', fontSize: 18 }} />;
  }
  if (mimeType?.includes('zip') || mimeType?.includes('archive') || mimeType?.includes('compressed')) {
    return <FileZipOutlined style={{ color: '#722ed1', fontSize: 18 }} />;
  }

  // Code files by MIME type
  const codeTypes = ['javascript', 'python', 'java', 'cpp', 'c', 'go', 'rust', 'typescript', 'x-python', 'x-java'];
  if (codeTypes.some(type => mimeType?.includes(type))) {
    return <FileTextOutlined style={{ color: '#1890ff', fontSize: 18 }} />;
  }

  return <FileOutlined style={{ fontSize: 18 }} />;
};

/**
 * Format file size in human-readable format
 */
export const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const value = bytes / Math.pow(1024, i);

  // Don't show decimals for bytes
  if (i === 0) {
    return `${value} ${sizes[i]}`;
  }

  // Show decimals for larger units, but avoid unnecessary precision
  return `${value.toFixed(value < 10 ? 1 : 0)} ${sizes[i]}`;
};

/**
 * Get file type label
 */
export const getFileTypeLabel = (item) => {
  if (item.type === 'folder') return 'Folder';

  const mimeType = item.mime_type || '';

  // Handle .ccapp files
  if (mimeType === 'application/x-ccapp+json') {
    return 'App';
  }

  if (mimeType.startsWith('image/')) return 'Image';
  if (mimeType === 'application/pdf') return 'PDF';
  if (mimeType.includes('word')) return 'Document';
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'Spreadsheet';
  if (mimeType.includes('zip') || mimeType.includes('archive')) return 'Archive';
  if (mimeType.includes('text') || mimeType.includes('code')) return 'Text';

  // Extract extension as fallback
  const parts = item.name.split('.');
  if (parts.length > 1) {
    const ext = parts.pop().toUpperCase();
    return ext;
  }

  return 'File';
};

/**
 * Calculate storage quota percentage
 */
export const getQuotaPercentage = (used, total) => {
  if (!total || total === 0) return 0;
  return Math.round((used / total) * 100);
};

/**
 * Get quota status color based on usage percentage
 */
export const getQuotaStatusColor = (percentage) => {
  if (percentage >= 90) return '#ff4d4f'; // Red - critical
  if (percentage >= 75) return '#faad14'; // Orange - warning
  return '#52c41a'; // Green - ok
};
